'use client'

import { create } from 'zustand'

export interface TourRect {
  top: number
  left: number
  width: number
  height: number
}

interface TourState {
  open: boolean
  step: number
  // The shell (and this component with it) fully remounts on every route
  // change, so local useState can't survive a step's navigation — keeping the
  // last-measured spotlight rect here lets the ring animate smoothly from its
  // old position to the new one instead of vanishing and reappearing.
  lastRect: TourRect | null
  openTour: () => void
  closeTour: () => void
  setStep: (step: number) => void
  setLastRect: (rect: TourRect | null) => void
}

export const useTourStore = create<TourState>((set) => ({
  open: false,
  step: 0,
  lastRect: null,
  openTour: () => set({ open: true, step: 0, lastRect: null }),
  closeTour: () => set({ open: false }),
  setStep: (step) => set({ step }),
  setLastRect: (lastRect) => set({ lastRect }),
}))

const SEEN_KEY_PREFIX = 'nivex.dashboardTourSeen.'

export function hasSeenDashboardTour(investorId: string): boolean {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(SEEN_KEY_PREFIX + investorId) === '1'
}

export function markDashboardTourSeen(investorId: string): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(SEEN_KEY_PREFIX + investorId, '1')
}

// Module-scope, not React state: `InvestorShell` (which runs the "auto-open on first visit"
// check) remounts on every dashboard route change, so a plain effect re-runs that check on every
// single page landed on. Gating the *attempt* itself to once per full page load — instead of only
// gating on the localStorage flag inside the effect — makes the very first attempt immune to
// React Strict Mode's dev-only double-invoked effects and to rapid mount/unmount cycles during
// the initial auth/gate redirect, both of which could otherwise race the localStorage read/write.
let autoOpenAttempted = false

export function tryAutoOpenDashboardTour(investorId: string): void {
  if (autoOpenAttempted) return
  autoOpenAttempted = true
  if (hasSeenDashboardTour(investorId)) return
  markDashboardTourSeen(investorId)
  useTourStore.getState().openTour()
}
